import React, { useCallback, useContext, useMemo } from 'react';
import {
  EChannelType,
  EGiftPaymentStep,
  TChannelDetail,
  TSendGiftFree,
  TSendMessage,
} from 'types';
import { TGift } from 'types/public';
import { ChatContext } from 'components/Chat/context';
import { Text, GiftItem, GiftPaymentStep, ModalGift } from 'components';
import { useToast, useUser } from 'hooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  queryKeys,
  TEXT_STRING,
  routes,
  ChargePoint,
} from '../../../constants';
import { getGiftList, sendGiftFree } from 'api';
import { useUserStore } from 'store';
import { useNavigate } from 'react-router-dom';

type Props = {
  sendMessage: (payload: TSendMessage) => Promise<void>;
  femaleId?: number;
  showGift?: boolean;
  channelData?: TChannelDetail;
};

const GiftBag = ({
  sendMessage,
  femaleId,
  showGift = false,
  channelData,
}: Props) => {
  // Hooks
  const navigate = useNavigate();
  const { stepPaymentGift, setStepPaymentGift } = useContext(ChatContext);
  const { lockedGift, refetchPoints } = useUser();
  const { isAuthenticated } = useUserStore();
  const { toastError, toastSuccess } = useToast();

  // States
  const [openGift, setOpenGift] = React.useState<boolean>(false);
  const [selectedGift, setSelectedGift] = React.useState<TGift>();

  // Query
  const { data, refetch } = useQuery({
    queryKey: [queryKeys.GIFT_LIST],
    queryFn: () => getGiftList(channelData?.channelId),
    enabled: channelData?.type === EChannelType.PRIVATE && isAuthenticated,
  });

  // Mutations
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (body: TSendGiftFree) => {
      return sendGiftFree(body);
    },
  });

  // Memo
  const listGift = useMemo(
    () => data?.map((gift) => ({ ...gift, isLocked: lockedGift(gift) })),
    [data, lockedGift],
  );

  // Callbacks
  const handleSendGift = useCallback(async () => {
    try {
      if (selectedGift?.price === 0 && femaleId) {
        await mutateAsync({ femaleId, giftId: selectedGift?.id });
        toastSuccess(TEXT_STRING.COMMON.SUCCESS);
        setSelectedGift(undefined);
        sendMessage({ gift: selectedGift, message: '' });
        refetch();
      } else {
        setStepPaymentGift(EGiftPaymentStep.CHOOSE_METHOD);
      }
      setOpenGift(false);
    } catch (error) {
      toastError(error as Error);
    }
  }, [
    selectedGift,
    femaleId,
    mutateAsync,
    toastSuccess,
    sendMessage,
    refetch,
    setStepPaymentGift,
    toastError,
  ]);

  const gotoChargePoint = React.useCallback(
    () => navigate(routes.BUY_POINTS),
    [navigate],
  );

  return (
    <>
      {showGift && (
        <div className="w-full h-2/3 grid grid-cols-4 p-5 gap-5 overflow-y-auto">
          <div className="cursor-pointer" onClick={gotoChargePoint}>
            <div className="w-full relative flex justify-center items-center">
              <img src={ChargePoint} alt="" />
            </div>
            <Text bold center>
              {TEXT_STRING.GIFT.CHARGE_POINT}
            </Text>
          </div>
          {listGift?.map((gift) => (
            <GiftItem
              key={gift.id}
              gift={gift}
              onClick={() => {
                setSelectedGift(gift);
                setOpenGift(true);
              }}
              isLocked={gift.isLocked}
            />
          ))}
        </div>
      )}
      <GiftPaymentStep
        onNextStep={() => setStepPaymentGift(+(stepPaymentGift || 0) + 1)}
        onPreviousStep={() => setStepPaymentGift(+(stepPaymentGift || 0) - 1)}
        giftId={selectedGift?.id || 0}
        onClose={() => setStepPaymentGift(undefined)}
        step={stepPaymentGift}
        femaleId={femaleId}
        onSendMessage={(gift) => {
          sendMessage({ gift, message: '' });
          refetchPoints();
        }}
        redirect={
          channelData?.channelId
            ? routes.CONVERSATION.replace(':id', channelData?.channelId)
            : ''
        }
      />

      <ModalGift
        open={openGift}
        onClose={() => setOpenGift(false)}
        onConfirm={handleSendGift}
        gift={selectedGift}
        isLocked={selectedGift?.isLocked}
        loading={isPending}
      />
    </>
  );
};

export default GiftBag;
